import * as React from "react";
import "./Report.css";
import { Alert, Box, CircularProgress, Container, Skeleton, Snackbar, Tab, Tabs } from "@mui/material";
import { a11yProps, TabPanel } from "../../../components/TabPanel/TabPanel";
import { useEffect, useState } from "react";
import DailyReport from "./DailyReport";
import SummaryReport from "./SummaryReport";
import { ConfidenceIntervalSwitch } from "./Switches/ConfidenceIntervalSwitch";
import { useUserContext } from "../../../providers/useUserProvider";
import { toTemporalZonedDateTime, startOf, endOf, toDate as temporalToDate } from "../../../shared/lib/temporal";

import { Device } from "../../../types/Device";
import { Count, CountType } from "../../../types/Count";

import { Deployment } from "../../../types/Deployment";
import { useFetchRawCounts } from "../../../hooks/useFetchRawCounts";
import { DetectionParams } from "../../../services/TaglessApi/TaglessApiService";
import { useProcessedCounts } from "../../../hooks/useProcessedCounts";
import { useFetchPipelineMetrics } from "../../../hooks/useFetchPipelineMetrics";
import { useFetchDevice } from "../../../hooks/useFetchDevice";
import { appDefaultTz } from "../../../providers/ApiClientProvider";
import { DeploymentNavLayout } from "../../../widgets/deployment-nav";

export type DeviceCounts = Record<
    string,
    {
        device: Device;
        counts: Count[];
    }
>;

interface ErrorState {
    hasError: boolean;
    message: string;
}

const CI_TOGGLE_KEY = "CI_SWITCH_ENABLED";

const Report = ({ setErrorState }: { setErrorState: (state: ErrorState) => void }) => {
    // Site
    const { selectedSite, timezoneDetails } = useUserContext();
    const timezone = timezoneDetails?.timezone || appDefaultTz;

    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(null);

    // Page controls
    const [activeTab, setActiveTab] = useState(0);
    const [ciSwitchEnabled, setCISwitchEnabled] = useState(false);
    const [ciSwitchChecked, setCISwitchChecked] = useState(() => {
        return localStorage.getItem(CI_TOGGLE_KEY) === "true";
    });

    const initialLoadDone = React.useRef(false);

    const { data: devices, isLoading: devicesLoading } = useFetchDevice(selectedSite, selectedDeployment);

    const detectionParams: DetectionParams = React.useMemo(() => {
        if (!selectedDeployment || !timezone) {
            return {
                startTimestamp: undefined,
                endTimestamp: undefined,
                interval: undefined,
            };
        }
        const nowZdt = toTemporalZonedDateTime(new Date(), timezone);
        const deploymentEndZdt = toTemporalZonedDateTime(new Date(selectedDeployment.endTime), timezone);

        const endZdt = Temporal.ZonedDateTime.compare(deploymentEndZdt, nowZdt) === 1 ? nowZdt : deploymentEndZdt;

        // Add 1 second to make endTimestamp exclusive similar to previous logic
        const adjustedEndZdt = endZdt.add({ seconds: 1 });

        const startZdt = startOf(toTemporalZonedDateTime(selectedDeployment.startTime, timezone), "day");

        // If deployment hasn't started yet, startTimestamp would be after endTimestamp
        if (Temporal.ZonedDateTime.compare(startZdt, adjustedEndZdt) >= 0) {
            return {
                startTimestamp: undefined,
                endTimestamp: undefined,
                interval: undefined,
            };
        }

        return {
            startTimestamp: temporalToDate(startZdt).toISOString(),
            endTimestamp: temporalToDate(adjustedEndZdt).toISOString(),
            interval: "1h",
        };
    }, [selectedDeployment, timezone]);

    // Counts
    const {
        data: rawCountArray,
        isPending: isCountPending,
        isError,
        error,
    } = useFetchRawCounts(devices || [], detectionParams, selectedDeployment?.id);

    const allDetections = rawCountArray.flat();

    const pipelineIds = Array.from(new Set(allDetections.map((d) => d.pipelineID)));

    const pipelineMetricResult = useFetchPipelineMetrics(pipelineIds);

    // Handle count and pipeline errors
    useEffect(() => {
        if (isError) {
            setErrorState({
                hasError: true,
                message: error?.message || `unable to fetch count`,
            });
        } else if (pipelineMetricResult.isError) {
            setErrorState({
                hasError: true,
                message: pipelineMetricResult.error?.message || `unable to fetch pipeline`,
            });
        }
    }, [isError, error, pipelineMetricResult.isError, pipelineMetricResult.error, setErrorState]);

    const { counts, ciEnabled } = useProcessedCounts(
        devices || [],
        rawCountArray,
        pipelineMetricResult.data,
        selectedDeployment,
        timezone,
    );

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    useEffect(() => {
        if (ciEnabled === ciSwitchEnabled) return;
        if (typeof ciEnabled === "boolean") {
            setCISwitchEnabled(ciEnabled);
            if (!ciEnabled) {
                setCISwitchChecked(false);
            }
        }
        if (ciSwitchEnabled) {
            const savedPreference = localStorage.getItem(CI_TOGGLE_KEY) === "true";
            setCISwitchChecked(savedPreference);
            initialLoadDone.current = true;
        }
    }, [ciEnabled, counts, ciSwitchEnabled]);

    const handleCISwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        localStorage.setItem(CI_TOGGLE_KEY, `${event.target.checked}`);
        setCISwitchChecked(event.target.checked);
    };

    const handleDateChange = React.useCallback(
        (date: Date | null) => {
            if (date && selectedDate) {
                const a = toTemporalZonedDateTime(date, timezone).toPlainDate();
                const b = toTemporalZonedDateTime(selectedDate, timezone).toPlainDate();
                if (Temporal.PlainDate.compare(a, b) === 0) return;
            }
            setSelectedDate(date);
        },
        [timezone, selectedDate],
    );

    const handleDeploymentChange = React.useCallback(
        (deployment: Deployment | null) => {
            if (deployment && selectedDeployment && deployment.id === selectedDeployment.id) return;
            setSelectedDeployment(deployment);
        },
        [selectedDeployment],
    );

    const isDeploymentNotStarted =
        !!selectedDeployment &&
        detectionParams.startTimestamp === undefined &&
        detectionParams.endTimestamp === undefined;

    const isLoading =
        !selectedDeployment || devicesLoading || isCountPending || pipelineMetricResult.isPending || !counts;

    return (
        <Box sx={{ width: "100%" }}>
            <Snackbar
                open={isDeploymentNotStarted}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                sx={{ mt: 5 }}
            >
                <Alert severity="info" variant="filled" elevation={6}>
                    This deployment has not started yet. Data will appear from{" "}
                    {new Date(selectedDeployment?.startTime ?? "").toLocaleString(undefined, {
                        timeZone: timezone,
                        dateStyle: "medium",
                        timeStyle: "short",
                    })}
                    .
                </Alert>
            </Snackbar>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        justifyContent: "space-between",
                        alignContent: "center",
                        alignItems: "center",
                    }}
                >
                    <DeploymentNavLayout
                        timezone={timezone}
                        siteId={selectedSite!.id}
                        hideNavButtons={activeTab !== 1}
                        onDateChange={handleDateChange}
                        onDeploymentChange={handleDeploymentChange}
                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        justifyContent: "space-between",
                        alignContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="report tabs">
                        <Tab label="Summary" {...a11yProps(0)} />
                        <Tab label="Daily Report" {...a11yProps(1)} />
                    </Tabs>

                    {ciEnabled !== undefined ? (
                        activeTab === 0 && (
                            <ConfidenceIntervalSwitch
                                checked={ciSwitchChecked}
                                onChange={handleCISwitchChange}
                                disabled={!ciSwitchEnabled || isCountPending}
                                deploymentSeason={selectedDeployment ? selectedDeployment.displayName : ""}
                            />
                        )
                    ) : (
                        <Skeleton variant="rectangular" />
                    )}
                </Box>
            </Box>
            {!isLoading ? (
                <React.Fragment>
                    <TabPanel value={activeTab} index={0}>
                        <SummaryReport
                            data={counts}
                            reportedCountType={ciSwitchChecked ? CountType.Adjusted : CountType.Predicted}
                            detectionParams={detectionParams}
                        />
                    </TabPanel>
                    <TabPanel value={activeTab} index={1}>
                        <DailyReport
                            deviceCounts={counts}
                            devices={devices}
                            setErrorState={setErrorState}
                            date={selectedDate}
                            deployment={selectedDeployment}
                        />
                    </TabPanel>
                </React.Fragment>
            ) : (
                <Container
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        paddingTop: "5%",
                        justifyContent: "center",
                        alignContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress />
                </Container>
            )}
        </Box>
    );
};

export default Report;
